/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
import React, { useCallback, useMemo, useState } from 'react'

import ProductCaseRow from './ProductCaseRow'
import ProductSplitRow from './ProductSplitRow'
import useViewMode from '../../hooks/useViewMode'

const hasSplitCasePurchase = (p) => p.splitCasePurchase && Object.keys(p.splitCasePurchase).length > 0

/**
 * Re-skinned equivalent of ShowListProducts (create order) /
 * ShowListProductUpdate (edit order). All the actual quantity/limit/pricing
 * math still lives in useProductOrder + useOrderSummary - this component only
 * decides *which* rows to render and wires their per-row callbacks, using
 * useCallback so each row-render function is stable across re-renders.
 */
const ProductListSection = ({
  defaultItemView,
  edittingOrder,
  products,
  productOrderRender,
  productOrderRenderIndividual,
  newOrderProductsIndividual,
  quantities,
  setQuantities,
  findProductById,
  calculatePricePerProduct,
  checkMoreThanLimitPerOrder,
  getValueSelect,
  handleChangeSubItem,
  changeProductQuantity,
  changeProductQuantityIndividual,
  changeProductQuantityInput,
  addAnotherOrder,
  deleteItemOrder,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  // const { viewMode, setListView, setGridView, isListView, isGridView } = useViewMode(defaultItemView)
  const viewMode = 'list'

  const visibleProducts = useMemo(() => {
    if (edittingOrder || !searchTerm) return productOrderRender
    const term = searchTerm.toLowerCase()
    return productOrderRender.filter((p) => p.name.toLowerCase().includes(term))
  }, [edittingOrder, productOrderRender, searchTerm])

  const getIndividualQuantity = useCallback(
    (id, index) => newOrderProductsIndividual.find((item) => item.id === id && item.index === index)?.quantity || 0,
    [newOrderProductsIndividual]
  )

  const productOrderRenderWithSplitCase = useMemo(() => {
    if (!edittingOrder) return []
    return productOrderRender.map((p) => ({
      ...p,
      quantityIndividual: getIndividualQuantity(p.id, p.index),
    }))
  }, [edittingOrder, productOrderRender, getIndividualQuantity])

  const onlyIndividualProducts = useMemo(() => {
    if (!edittingOrder) return []
    return productOrderRenderIndividual.filter(
      (ind) => !productOrderRender.some((p) => p.id === ind.id && p.index === ind.index)
    )
  }, [edittingOrder, productOrderRenderIndividual, productOrderRender])

  const renderCaseRow = useCallback(
    (p) => {
      const quantityEntry = findProductById(p.id, p.index)
      const soldOut = p.orderCount >= p.maxQuantity
      const price = calculatePricePerProduct(p.id, p.index, p.price)

      return (
        <ProductCaseRow
          key={`case-${p.id}-${p.index}`}
          product={p}
          price={price}
          quantityEntry={quantityEntry}
          soldOut={soldOut}
          edittingOrder={edittingOrder}
          canAddAnother={!!p.subItems?.length && !checkMoreThanLimitPerOrder(p.id, p.limitPerOrder)}
          canRemove={p.index > 1}
          getValueSelect={(type) => getValueSelect(p.id, p.index, type)}
          onSubItemChange={(subItem, value) => handleChangeSubItem(p.id, subItem, value, p.index)}
          onDecrement={() => {
            const currentQuantity = quantityEntry?.quantity || 0
            if (currentQuantity > 0) {
              changeProductQuantity({ pid: p.id, number: currentQuantity - 1, index: p.index })
            }
          }}
          onIncrement={() => {
            const currentQuantity = quantityEntry?.quantity || 0
            if (
              (p.limitPerOrder === 0 || currentQuantity < p.limitPerOrder) &&
              p.maxQuantity - p.newOrderCount > 0 &&
              !checkMoreThanLimitPerOrder(p.id, p.limitPerOrder)
            ) {
              changeProductQuantity({ pid: p.id, number: currentQuantity + 1, index: p.index })
            }
          }}
          onQuantityInputChange={(number) => changeProductQuantityInput({ pid: p.id, number, index: p.index })}
          onQuantityInputBlur={(number) => changeProductQuantity({ pid: p.id, number, index: p.index })}
          onAddAnother={() => addAnotherOrder(p.id, products)}
          onRemove={() => deleteItemOrder(p.id, p.index)}
        />
      )
    },
    [
      findProductById,
      calculatePricePerProduct,
      edittingOrder,
      checkMoreThanLimitPerOrder,
      getValueSelect,
      handleChangeSubItem,
      changeProductQuantity,
      changeProductQuantityInput,
      addAnotherOrder,
      deleteItemOrder,
      products,
    ]
  )

  const renderCreateSplitRow = useCallback(
    (p) => {
      const key = `${p.id}-${p.index}-split`
      const current = quantities[key] ?? 0
      const soldOut = p.maxQuantity !== 1000 && p.orderCount >= p.maxQuantity

      return (
        <ProductSplitRow
          key={`split-${p.id}-${p.index}`}
          splitCasePurchase={p.splitCasePurchase}
          quantity={current}
          soldOut={soldOut}
          edittingOrder={edittingOrder}
          onDecrement={() => {
            if (current > 0) {
              const newValue = current - 1
              setQuantities((q) => ({ ...q, [key]: newValue }))
              changeProductQuantity({ pid: p.id, number: newValue, index: p.index, isSplit: true })
            }
          }}
          onIncrement={() => {
            const newValue = current + 1
            setQuantities((q) => ({ ...q, [key]: newValue }))
            changeProductQuantity({ pid: p.id, number: newValue, index: p.index, isSplit: true })
          }}
          onInputChange={(val) => setQuantities((q) => ({ ...q, [key]: val }))}
          onInputCommit={(val) => {
            setQuantities((q) => ({ ...q, [key]: val }))
            changeProductQuantity({ pid: p.id, number: val, index: p.index, isSplit: true })
          }}
        />
      )
    },
    [quantities, setQuantities, edittingOrder, changeProductQuantity]
  )

  const renderEditSplitRow = useCallback(
    (p, fallbackQuantity) => {
      const key = `${p.id}-${p.index}-split`
      const current = quantities[key] ?? fallbackQuantity
      const soldOut = p.maxQuantity !== 1000 && p.orderCount >= p.maxQuantity

      return (
        <ProductSplitRow
          key={`split-${p.id}-${p.index}`}
          splitCasePurchase={p.splitCasePurchase}
          quantity={current}
          soldOut={soldOut}
          edittingOrder={edittingOrder}
          onDecrement={() => {
            if (current > 0) {
              const newValue = current - 1
              setQuantities((q) => ({ ...q, [key]: newValue }))
              changeProductQuantityIndividual({ pid: p.id, number: newValue, index: p.index, isSplit: true })
            }
          }}
          onIncrement={() => {
            const newValue = current + 1
            setQuantities((q) => ({ ...q, [key]: newValue }))
            changeProductQuantityIndividual({ pid: p.id, number: newValue, index: p.index, isSplit: true })
          }}
          onInputChange={(val) => setQuantities((q) => ({ ...q, [key]: val }))}
          onInputCommit={(val) => {
            setQuantities((q) => ({ ...q, [key]: val }))
            changeProductQuantityIndividual({ pid: p.id, number: val, index: p.index, isSplit: true })
          }}
        />
      )
    },
    [quantities, setQuantities, edittingOrder, changeProductQuantityIndividual]
  )

  if (!productOrderRender?.length && !productOrderRenderIndividual?.length) return null

  return (
    <React.Fragment>
      {!edittingOrder && (
        <div className="edv2-search">
          <svg className="edv2-search__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          <input
            placeholder="Search product"
            className="edv2-search__input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}
      <div className={`edv2-products edv2-products--${viewMode}`}>
        <div className="edv2-products__header">
          <div className="edv2-products__title">ITEMS</div>
          {/* <div className="edv2-view-toggle">
            <button
              type="button"
              className={`edv2-view-toggle__btn ${isListView ? 'is-active' : ''}`}
              onClick={setListView}
              title="List view"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
            </button>
            <button
              type="button"
              className={`edv2-view-toggle__btn ${isGridView ? 'is-active' : ''}`}
              onClick={setGridView}
              title="Tile view"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"></rect><rect x="14" y="3" width="7" height="7" rx="1"></rect><rect x="3" y="14" width="7" height="7" rx="1"></rect><rect x="14" y="14" width="7" height="7" rx="1"></rect></svg>
            </button>
            abc
          </div> */}
        </div>
        <div className="edv2-products__list">
          {!edittingOrder &&
            visibleProducts.map((p) => (
              <React.Fragment key={`${p.id}-${p.index}`}>
                {renderCaseRow(p)}
                {hasSplitCasePurchase(p) && renderCreateSplitRow(p)}
              </React.Fragment>
            ))}

          {edittingOrder &&
            productOrderRenderWithSplitCase.map((p) => (
              <React.Fragment key={`${p.id}-${p.index}`}>
                {renderCaseRow(p)}
                {hasSplitCasePurchase(p) && renderEditSplitRow(p, p.quantityIndividual)}
              </React.Fragment>
            ))}

          {edittingOrder &&
            onlyIndividualProducts.map((p) => (
              <React.Fragment key={`only-${p.id}-${p.index}`}>
                {renderCaseRow(p)}
                {hasSplitCasePurchase(p) && renderEditSplitRow(p, getIndividualQuantity(p.id, p.index))}
              </React.Fragment>
            ))}
        </div>
      </div>
    </React.Fragment>
  )
}

export default ProductListSection
